import React from 'react';
import { Plane, Tag, Luggage, Utensils, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { getFareTypeInfo } from '../utils/fareTypeMapping';

const FlightCard = ({
  flight,
  selectedFare,
  index,
  cheapestPrice,
  airlineLogos,
  isExpanded,
  onToggleExpand,
  onBook,
  onFareSelect,
  className = ''
}) => {
  // Use selected fare if available, otherwise use default flight data.
  // Bug-fix Feb-2026 · The summary chips (baggage / meal / seats / refundable)
  // were reading `flight.*` directly so they stayed frozen on the cheapest
  // fare's amenity values even after the user picked a different fare card.
  // These display* derivations mirror the existing displayPrice/displayFareType
  // pattern so every chip honours the user's selection.
  const displayPrice = selectedFare?.price || flight.price;
  const displayFareType = selectedFare?.fareType || flight.fareType;
  const displayBaggage = selectedFare?.baggage ?? flight.baggage;
  const displayCabinBaggage = selectedFare?.cabinBaggage ?? flight.cabinBaggage;
  const displayMealIncluded = selectedFare?.mealIncluded ?? flight.mealIncluded;
  const displaySeatsLeft = selectedFare?.seatsLeft ?? flight.seatsLeft;
  const isSoldOut = flight.isSoldOut || displaySeatsLeft === 0;
  // refundable: prefer the boolean if present, else derive from the
  // Tripjack `refundableType` enum (1 = Refundable, 2 = Partially refundable).
  const _selRt = selectedFare?.refundableType;
  const displayRefundable = _selRt != null
    ? (_selRt === 1 || _selRt === 2)
    : (flight.refundable
        ?? (flight.refundableType === 1 || flight.refundableType === 2));
  
  return (
    <div
      data-testid={`flight-card-${flight.source || 'unknown'}-${flight.priceId || flight.id || index}`}
      className={`bg-white rounded-2xl border shadow-sm transition-all duration-300 overflow-hidden group ${
      isSoldOut ? 'opacity-60' : 'hover:shadow-lg'
    } ${
      displayPrice === cheapestPrice ? 'border-[#659A9B]/40 ring-1 ring-[#659A9B]/20' : 'border-gray-200/80'
    } ${className}`}>
      {/* Cheapest tag */}
      {!isSoldOut && displayPrice === cheapestPrice && index === 0 && (
        <div className="bg-[#659A9B] text-white text-[10px] font-bold px-4 py-1 flex items-center gap-1.5">
          <Tag className="w-3 h-3" /> CHEAPEST
        </div>
      )}

      {/* Sold Out tag */}
      {isSoldOut && (
        <div data-testid="flight-card-sold-out-badge" className="bg-gray-700 text-white text-[10px] font-bold px-4 py-1 flex items-center gap-1.5">
          <AlertCircle className="w-3 h-3" /> SOLD OUT
        </div>
      )}
      
      {/* Offline/Block Ticket tag */}
      {flight.source === "offline" && (
        <div className="bg-amber-100 text-amber-800 text-[10px] font-bold px-4 py-1 flex items-center gap-1.5 border-b border-amber-200">
          🎫 BLOCK TICKET
        </div>
      )}

      <div className="p-5">
        <div className="flex items-center">
          {/* Airline */}
          <div className="flex items-center gap-3 w-[160px] flex-shrink-0">
            <div className="w-11 h-11 flex items-center justify-center flex-shrink-0">
              {airlineLogos[flight.airlineCode]?.square ? (
                <img
                  src={airlineLogos[flight.airlineCode].square}
                  alt={flight.airline}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Plane className="w-5 h-5 text-[#659A9B]" />
              )}
            </div>
            <div>
              <p className="font-bold text-sm text-[#1B375B]">{flight.airline}</p>
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="text-[11px] text-gray-400">{flight.flightNumber}</p>
                {displayFareType && (() => {
                  const fareInfo = getFareTypeInfo(displayFareType);
                  return (
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${fareInfo.bgColor} ${fareInfo.textColor} ${fareInfo.borderColor} border`}>
                      {fareInfo.label}
                    </span>
                  );
                })()}
              </div>
              {flight.isCodeshare && flight.operatingAirlineCode && (
                <p
                  data-testid="flight-codeshare-chip"
                  className="text-[10px] text-gray-500 mt-0.5"
                >
                  Operated by{' '}
                  <span className="font-semibold text-[#1B375B]">
                    {flight.operatingAirline || flight.operatingAirlineCode}
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Departure */}
          <div className="text-center w-[100px]">
            <p className="text-2xl font-extrabold text-[#1B375B] leading-none">{flight.departureTime}</p>
            <p className="text-xs text-gray-400 mt-0.5">{flight.from}</p>
            <p className="text-[10px] text-gray-500 font-medium">{flight.fromCity}</p>
          </div>

          {/* Duration line */}
          <div className="flex-1 flex flex-col items-center px-3 mx-2">
            <div className="flex items-center gap-0 w-full">
              <div className="w-2 h-2 rounded-full border-2 border-[#1B375B]" />
              <div className="h-[1.5px] flex-1 bg-gradient-to-r from-[#1B375B]/30 via-[#659A9B]/50 to-[#1B375B]/30 relative">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-1">
                  <Plane className="w-3.5 h-3.5 text-[#659A9B] rotate-90" />
                </div>
              </div>
              <div className="w-2 h-2 rounded-full bg-[#659A9B]" />
            </div>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap justify-center">
              <span className="text-[11px] text-gray-400 font-medium">{flight.duration}</span>
              <span className="text-[10px] bg-[#659A9B]/10 text-[#335E77] font-semibold px-2 py-0.5 rounded-full">{flight.stops}</span>
              {/* Audit §7 L4 — `sI.so` stopover-airports chip when supplier ships it */}
              {Array.isArray(flight.stopAirports) && flight.stopAirports.length > 0 ? (
                <span
                  className="text-[10px] text-gray-500 font-medium"
                  data-testid="flight-card-stop-airports"
                  title={`Stops: ${flight.stopAirports.join(' · ')}`}
                >
                  via {flight.stopAirports.join(' · ')}
                </span>
              ) : null}
            </div>
          </div>

          {/* Arrival */}
          <div className="text-center w-[100px]">
            <p className="text-2xl font-extrabold text-[#1B375B] leading-none">
              {flight.arrivalTime}
              {/* Audit §7 L3 — `sI.Iand` next-day-arrival indicator */}
              {flight.nextDayArrival ? (
                <span
                  className="ml-1 text-[10px] align-top text-amber-600 font-bold"
                  title="Arrives next day"
                  data-testid="flight-card-next-day-arrival"
                >
                  +1
                </span>
              ) : null}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{flight.to}</p>
            <p className="text-[10px] text-gray-500 font-medium">{flight.toCity}</p>
          </div>

          {/* Price + Book */}
          <div className="w-[220px] flex-shrink-0 flex items-center justify-end gap-5 pl-4">
            <div className="text-right">
              <p className="text-xl font-extrabold text-[#1B375B] leading-none">₹{displayPrice.toLocaleString()}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">per adult</p>
            </div>
            <button
              onClick={isSoldOut ? undefined : onBook}
              disabled={isSoldOut}
              data-testid="flight-card-book-button"
              className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 shadow-sm flex-shrink-0 ${
                isSoldOut
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#1B375B] text-white hover:bg-[#659A9B] hover:shadow-md'
              }`}
            >
              {isSoldOut ? 'Sold Out' : 'Book Now'}
            </button>
          </div>
        </div>

        {/* Bottom row: details toggle + tags */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-[11px] text-gray-500">
              <Luggage className="w-3 h-3" />{' '}
              {[displayCabinBaggage, displayBaggage].filter(Boolean).join(' + ') || '—'}
            </span>
            {displayMealIncluded && (
              <span className="flex items-center gap-1 text-[11px] text-green-600 font-medium">
                <Utensils className="w-3 h-3" /> Meal Included
              </span>
            )}
            {displaySeatsLeft && displaySeatsLeft <= 9 && (
              <span className="flex items-center gap-1 text-[11px] text-orange-600 font-semibold">
                <AlertCircle className="w-3 h-3" /> {displaySeatsLeft} seats left
              </span>
            )}
            {displayRefundable && (
              <span className="text-[10px] bg-green-50 text-green-700 font-semibold px-2 py-0.5 rounded">
                Refundable
              </span>
            )}
          </div>
          <button
            onClick={() => onToggleExpand(flight.id)}
            className="flex items-center gap-1 text-xs text-[#659A9B] hover:text-[#335E77] font-semibold transition-colors"
          >
            Flight Details
            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Multiple Fare Options - Inside Card */}
      {flight.fareOptions && flight.fareOptions.length > 1 && (
        <div className="border-t border-gray-200/80 bg-gray-50/30 px-5 py-3">
          <p className="text-[10px] font-bold text-[#1B375B] uppercase tracking-wider mb-2">
            {flight.fareOptions.length} fare options - Select one:
          </p>
          <div className="grid grid-cols-5 gap-2">
            {flight.fareOptions.map((fare, idx) => {
              const isSelected = selectedFare?.priceId === fare.priceId;
              const fareInfo = getFareTypeInfo(fare.fareType);
              
              return (
                <button
                  key={idx}
                  type="button"
                  data-testid={`flight-fare-option-${fare.priceId || idx}`}
                  disabled={isSoldOut}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onFareSelect && !isSoldOut) {
                      onFareSelect(fare);
                    }
                  }}
                  className={`p-2.5 border-2 rounded-lg transition-all text-left relative ${
                    isSoldOut ? 'opacity-50 cursor-not-allowed' : ''
                  } ${
                    isSelected
                      ? 'border-teal-500 bg-teal-50 shadow-md'
                      : 'bg-white border-gray-200 hover:border-teal-300 hover:shadow-sm'
                  }`}
                >
                  {/* Checkmark Badge - Top Right */}
                  {isSelected && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Fare Type Badge */}
                  <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded ${fareInfo.bgColor} ${fareInfo.textColor} mb-1.5`}>
                    {fareInfo.label}
                  </span>
                  
                  {/* Price */}
                  <p className="text-base font-extrabold text-[#1B375B] leading-tight">₹{fare.price.toLocaleString()}</p>
                  
                  {/* Baggage - Compact */}
                  <p className="text-[9px] text-gray-500 mt-0.5 truncate">
                    {[fare.cabinBaggage, fare.baggage].filter(Boolean).join(' · ') || '—'}
                  </p>
                  
                  {/* Refundable Badge */}
                  {fare.refundable && (
                    <span className="inline-block text-[8px] bg-green-50 text-green-700 font-bold px-1.5 py-0.5 rounded mt-1">
                      Refundable
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightCard;
